'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import { GROUP_KEYS } from 'getting-started-shared-tasks/gettingStartedGroupKeys';
import { ONBOARDING_MARKETER_INEXPERIENCED, ONBOARDING_MARKETER_WITH_CRM_EXPERIENCE, ONBOARDING_MARKETER_WITH_HUBSPOT_EXPERIENCE, ONBOARDING_SALES_LEADER_INEXPERIENCED, ONBOARDING_SALES_LEADER_WITH_CRM_EXPERIENCE, ONBOARDING_SALES_LEADER_WITH_HUBSPOT_EXPERIENCE, ONBOARDING_SALES_PERSON_INEXPERIENCED, ONBOARDING_SALES_PERSON_WITH_CRM_EXPERIENCE, ONBOARDING_SALES_PERSON_WITH_HUBSPOT_EXPERIENCE, ONBOARDING_SERVICE_FREE, ONBOARDING_CMS_SIMPLIFIED, ONBOARDING_OPS_FREE_INEXPERIENCED, ONBOARDING_OPS_FREE_EXPERIENCED, ONBOARDING_OPS_FREE_HUBSPOT_EXPERIENCED } from 'getting-started-shared-tasks/onboardingKeys';
import { ONBOARDING_VIEWS, setOnboardingSettings } from 'user-context/onboardingSettings';
import { setDefaultGroupKey } from 'user-context/onboarding';
import { CRM_SIGNUP, SALES_SIGNUP, SERVICE_SIGNUP, MARKETING_SIGNUP, CMS_FREE_SIGNUP } from '../constants/signupConstants';
import { EXPERIENCE_NONE_PARAM, EXPERIENCE_HAS_USED_HUBSPOT, EXPERIENCE_HAS_USED_CRM_PARAM, SURVEY_KEYS, SALES_REP_ROLE } from '../constants/surveyConstants';
import { HubletUrl } from '../utils/urls';

const convertExperience = surveyExperience => {
  if (surveyExperience === EXPERIENCE_HAS_USED_HUBSPOT) {
    return 'with_hubspot_experience';
  }

  if (surveyExperience === EXPERIENCE_HAS_USED_CRM_PARAM) {
    return 'with_crm_experience';
  }

  return 'inexperienced';
};
/**
 * Creates an onboarding key for the Sales Persona based on submitted job role and experience level
 * @param {string} surveyRole
 * @param {string} surveyExperience
 * @returns {string} Sales specific onboarding key
 */


export const createSalesOnboardingKey = (surveyRole, surveyExperience) => {
  if (!surveyRole || !surveyExperience) {
    return false;
  }

  const role = surveyRole === SALES_REP_ROLE ? 'person' : 'leader';
  const experience = convertExperience(surveyExperience);
  return `sales_${role}_${experience}`;
};
export const createSalesGroupKey = (surveyRole, surveyExperience) => {
  const isSalesRep = surveyRole === SALES_REP_ROLE;

  switch (surveyExperience) {
    case EXPERIENCE_NONE_PARAM:
      return isSalesRep ? GROUP_KEYS.INEXPERIENCED_SIMPLIFIED_SALESPERSON : GROUP_KEYS.INEXPERIENCED_SIMPLIFIED_SALES_LEADER;

    case EXPERIENCE_HAS_USED_CRM_PARAM:
      return isSalesRep ? GROUP_KEYS.experiencedCrmIc : GROUP_KEYS.experiencedCrmLeader;

    case EXPERIENCE_HAS_USED_HUBSPOT:
      return isSalesRep ? GROUP_KEYS.experiencedSalesIc : GROUP_KEYS.experiencedSalesLeader;

    default:
      return GROUP_KEYS.experiencedCrmLeader;
  }
};
export const getSalesInexperiencedGroupKey = surveyRole => {
  if (!surveyRole) {
    return GROUP_KEYS.INEXPERIENCED_SIMPLIFIED_SALES_LEADER;
  }

  return createSalesGroupKey(surveyRole, EXPERIENCE_NONE_PARAM);
};
export const createMarketingOnboardingKey = surveyExperience => {
  if (!surveyExperience) {
    return false;
  }

  const experience = convertExperience(surveyExperience);
  return `marketing_${experience}`;
};
export const getServiceOnboardingKey = () => {
  return ONBOARDING_SERVICE_FREE;
};
export const createOpsHubOnboardingKey = surveyExperience => {
  const experience = convertExperience(surveyExperience);
  return `ops_free_${experience}`;
};
export const getDefaultOnboardingSettings = survey => {
  const {
    SALES,
    MARKETING,
    SERVICE,
    CMS,
    OPS
  } = ONBOARDING_VIEWS;
  const crmExperience = survey[SURVEY_KEYS.CRM_EXPERIENCE];
  const jobRole = survey[SURVEY_KEYS.JOB_ROLE];
  const isSalesRep = jobRole === SALES_REP_ROLE;
  const defaultServiceSettings = {
    groupKey: GROUP_KEYS.services,
    onboardingKey: ONBOARDING_SERVICE_FREE
  };
  const cmsSettings = {
    [CMS]: {
      groupKey: GROUP_KEYS.cmsV3,
      onboardingKey: ONBOARDING_CMS_SIMPLIFIED
    }
  };
  const defaultNonExperienceSettings = {
    views: Object.assign({
      [SALES]: {
        groupKey: createSalesGroupKey(jobRole, EXPERIENCE_NONE_PARAM),
        onboardingKey: isSalesRep ? ONBOARDING_SALES_PERSON_INEXPERIENCED : ONBOARDING_SALES_LEADER_INEXPERIENCED
      },
      [MARKETING]: {
        groupKey: GROUP_KEYS.INEXPERIENCED_SIMPLIFIED_MARKETING,
        onboardingKey: ONBOARDING_MARKETER_INEXPERIENCED
      },
      [SERVICE]: {
        groupKey: GROUP_KEYS.INEXPERIENCED_SIMPLIFIED_SERVICE,
        onboardingKey: ONBOARDING_SERVICE_FREE
      },
      [OPS]: {
        onboardingKey: ONBOARDING_OPS_FREE_INEXPERIENCED
      }
    }, cmsSettings)
  };

  switch (crmExperience) {
    case EXPERIENCE_NONE_PARAM:
      return defaultNonExperienceSettings;

    case EXPERIENCE_HAS_USED_CRM_PARAM:
      return {
        views: Object.assign({
          [SALES]: {
            groupKey: createSalesGroupKey(jobRole, EXPERIENCE_HAS_USED_CRM_PARAM),
            onboardingKey: isSalesRep ? ONBOARDING_SALES_PERSON_WITH_CRM_EXPERIENCE : ONBOARDING_SALES_LEADER_WITH_CRM_EXPERIENCE
          },
          [MARKETING]: {
            groupKey: GROUP_KEYS.marketerSep2020,
            onboardingKey: ONBOARDING_MARKETER_WITH_CRM_EXPERIENCE
          },
          [SERVICE]: defaultServiceSettings,
          [OPS]: {
            onboardingKey: ONBOARDING_OPS_FREE_EXPERIENCED
          }
        }, cmsSettings)
      };

    case EXPERIENCE_HAS_USED_HUBSPOT:
      return {
        views: Object.assign({
          [SALES]: {
            groupKey: createSalesGroupKey(jobRole, EXPERIENCE_HAS_USED_HUBSPOT),
            onboardingKey: isSalesRep ? ONBOARDING_SALES_PERSON_WITH_HUBSPOT_EXPERIENCE : ONBOARDING_SALES_LEADER_WITH_HUBSPOT_EXPERIENCE
          },
          [MARKETING]: {
            groupKey: GROUP_KEYS.marketerSep2020,
            onboardingKey: ONBOARDING_MARKETER_WITH_HUBSPOT_EXPERIENCE
          },
          [SERVICE]: defaultServiceSettings,
          [OPS]: {
            onboardingKey: ONBOARDING_OPS_FREE_HUBSPOT_EXPERIENCED
          }
        }, cmsSettings)
      };

    default:
      return defaultNonExperienceSettings;
  }
};
export const getDefaultView = signupType => {
  switch (signupType) {
    case CRM_SIGNUP:
      return ONBOARDING_VIEWS.SALES;

    case MARKETING_SIGNUP:
      return ONBOARDING_VIEWS.MARKETING;

    case SALES_SIGNUP:
      return ONBOARDING_VIEWS.SALES;

    case SERVICE_SIGNUP:
      return ONBOARDING_VIEWS.SERVICE;

    case CMS_FREE_SIGNUP:
      return ONBOARDING_VIEWS.CMS;

    default:
      return ONBOARDING_VIEWS.SALES;
  }
};
export const setDefaultOnboardingSettings = (survey, hubOnboardingSettings, signupType, userId, portalId, logger, gates) => {
  if (!userId) {
    logger.error('No user id provided, skipping setting onboarding key');
    return Promise.resolve();
  }

  if (!portalId) {
    logger.error('No portal id provided, skipping setting onboarding key');
    return Promise.resolve();
  }

  const {
    view
  } = hubOnboardingSettings,
        overrideOnboardingSettings = _objectWithoutPropertiesLoose(hubOnboardingSettings, ["view"]);

  const onboardingSettings = getDefaultOnboardingSettings(survey, gates);

  if (view) {
    onboardingSettings.views[view] = Object.assign({}, onboardingSettings.views[view], {}, overrideOnboardingSettings);
    onboardingSettings.selectedView = view;
  }

  if (!onboardingSettings.selectedView) {
    onboardingSettings.selectedView = getDefaultView(signupType);
  }

  const apiUrl = HubletUrl.getApiUrl();
  const requestOptions = {
    hubletOriginOverride: apiUrl
  };
  const settingsRequests = [setOnboardingSettings(onboardingSettings, userId, portalId, requestOptions)]; // groupKey also needs to be set in usercontext-app for setting user's default task group

  const {
    groupKey
  } = hubOnboardingSettings;

  if (view && groupKey) {
    settingsRequests.push(setDefaultGroupKey(groupKey, requestOptions));
  }

  return Promise.all(settingsRequests);
};